import React from 'react';
import AnalyticsPageLayout from '../components/analytics/AnalyticsPageLayout';
import DownloadButton from '../components/analytics/DownloadButton';
import BreakdownPanel from '../components/impact/BreakdownPanel';
import ImpactStat from '../components/impact/ImpactStat';
import { formatCount } from '../components/impact/format';
import useImpactReport from '../hooks/useImpactReport';

const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';

// Learners outside any regular cohort. Self-paced learning has no
// graduation, so only learners, active learners and who they are.
function SelfPacedAnalytics() {
  const { data, isLoading, isError, errorStatus, refetch } =
    useImpactReport('/impact/self-paced');
  const selfPaced = data?.self_paced;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
      );
    }
    if (isError || !selfPaced) {
      return (
        <div className={`${CARD} p-8 text-center space-y-3`}>
          <p className="text-sm text-gray-600">
            {errorStatus === 403
              ? 'This report is only available to administrators.'
              : "We couldn't load self-paced learners."}
          </p>
          {(!errorStatus || errorStatus >= 500) && (
            <button
              type="button"
              onClick={() => refetch()}
              className="bg-claret-500 hover:bg-claret-400 px-4 py-1.5 rounded-md text-white text-sm"
            >
              Retry
            </button>
          )}
        </div>
      );
    }
    return (
      <>
        <div className="flex justify-end">
          <DownloadButton
            label="Download self-paced"
            path="/impact/export?view=self-paced"
            fallbackName="impact-self-paced.csv"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ImpactStat
            label="Self-paced learners"
            display={formatCount(selfPaced.learners)}
            suppressed={selfPaced.learners === null}
            info={data.definition}
          />
          <ImpactStat
            label="Active"
            display={formatCount(selfPaced.active)}
            suppressed={selfPaced.active === null}
            accent="#006366"
            detail="Made any progress: a quiz, an assignment or a completed week"
          />
        </div>
        <BreakdownPanel
          title="Who self-paced learners are"
          subtitle="Self-paced learning has no graduation, so only learner counts are shown."
          breakdowns={selfPaced.breakdowns}
        />
        <p className="text-xs text-gray-500">
          Counts below {data.suppression_threshold ?? 5} are shown as &quot;&lt;{' '}
          {data.suppression_threshold ?? 5}&quot; to protect learner privacy.
        </p>
      </>
    );
  };

  return (
    <AnalyticsPageLayout
      title="Self-paced Learners"
      description="Learners studying outside a regular cohort, and who they are."
    >
      {renderBody()}
    </AnalyticsPageLayout>
  );
}

export default SelfPacedAnalytics;
